import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProps,
} from "@chakra-ui/react";
import React, { PropsWithChildren, useLayoutEffect, useState } from "react";

import { asArray } from "../../../utils/array";

interface MainTabProps<T extends string> {
  id: T;
  displayName: JSX.Element | string;
  padBottom?: true;
  noPadding?: boolean;
}

export const MainTab = <T extends string>({
  children,
  padBottom,
}: PropsWithChildren<MainTabProps<T>>): JSX.Element => {
  if (padBottom)
    return (
      <Box
        pb="4"
        height="100%"
        display="flex"
        flexDir="column"
        overflowY="auto"
        overflowX="hidden"
      >
        {children}
      </Box>
    );
  return <>{children}</>;
};

type MainTab<T extends string> = React.ReactElement<MainTabProps<T>>;
type Child<T extends string> =
  | MainTab<T>
  | string
  | number
  | boolean
  | null
  | undefined;

interface MainTabsProps<T extends string>
  extends Omit<TabsProps, "index" | "onChange" | "defaultIndex"> {
  currentTab?: T;
  onTabChange?(tab: T): void;
  children: Child<T>[] | Child<T>;
}

const MainTabs = <T extends string>({
  currentTab,
  onTabChange,
  isLazy = true,
  lazyBehavior = "keepMounted",
  children,
  ...tabsProps
}: MainTabsProps<T>): JSX.Element => {
  const { tabs, tabIds, tabNames } = useTabData(children);

  const [tabIndex, setTabIndexState] = useState(0);
  const setTabIndex = (idx: number): void => {
    if (idx !== tabIndex) {
      setTabIndexState(idx);
      onTabChange?.(tabIds[idx]);
    }
  };

  // Set tab based on the `currentTab` prop
  // useLayoutEffect to avoid one frame of rendering the prior tab
  useLayoutEffect(() => {
    if (currentTab) {
      const idx = tabIds.findIndex((tabId) => tabId === currentTab);
      if (idx !== -1) {
        setTabIndex(idx);
      }
    }
  }, [currentTab, tabIds]);

  return (
    <Tabs
      display="flex"
      flexDir="column"
      height="100%"
      index={tabIndex}
      onChange={setTabIndex}
      isLazy={isLazy}
      lazyBehavior={lazyBehavior}
      bg={{ base: "inherit", lg: "gray.100" }}
      {...tabsProps}
    >
      <Flex
        justifyContent="center"
        px={{ base: "16px", lg: "32px" }}
        pt={{ base: "8px", lg: "24px" }}
        pb={{ base: "16px", lg: "24px" }}
      >
        <TabList
          alignItems="center"
          justifyContent="center"
          borderBottom="none"
          borderRadius="lg"
          bg="blackAlpha.100"
          w={tabNames.length > 2 ? "100%" : "auto"}
          maxWidth="540px"
          p="4px"
          gap="1"
        >
          {tabNames.map((tabName, idx) => {
            const isSelected = idx === tabIndex;
            return (
              <Tab
                _hover={{
                  backgroundColor: "white",
                }}
                _selected={{
                  background: "white",
                }}
                whiteSpace="nowrap"
                borderRadius="lg"
                color="gray.900 !important"
                data-testid={`${tabIds[idx]}-tab`}
                h="8"
                key={tabIds[idx]}
                py="6px"
                px="12px"
                flex="1 1 0%"
                minW="0"
                my="0"
              >
                <Box
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight="600"
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  width="100%"
                >
                  {typeof tabName === "function"
                    ? tabName({ isSelected })
                    : tabName}
                </Box>
              </Tab>
            );
          })}
        </TabList>
      </Flex>

      <TabPanels
        overflow="hidden"
        flex="1"
        minH="0"
        px={{ base: "16px", lg: "32px" }}
        borderColor="gray.100"
        borderTopWidth={{ base: "1px", lg: "0" }}
      >
        {tabs.map((tab, idx) => {
          const { noPadding } = tab.props;
          return (
            <TabPanel
              key={tabIds[idx]}
              h="100%"
              minH="0"
              borderColor="gray.200"
              borderRadius={{ base: "none", lg: "lg" }}
              borderWidth={{ base: "0", lg: "1px" }}
              bg="white"
              pt={noPadding ? 0 : { base: "16px", lg: "32px" }}
              pb={noPadding ? 0 : { base: "16px", lg: "32px" }}
              pl={noPadding ? 0 : { base: "0", lg: "32px" }}
              pr={noPadding ? 0 : { base: "0", lg: "32px" }}
              overflowY="auto"
              overflowX="hidden"
              display="flex"
              flexDir="column"
            >
              {tab}
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
};

export default MainTabs;

/**
 * Filter out falsy children and return tabs along with names and ids
 */
function useTabData<T extends string>(
  children: Child<T>[] | Child<T>
): {
  tabs: MainTab<T>[];
  tabIds: T[];
  tabNames: (string | JSX.Element | React.FC<{ isSelected: boolean }>)[];
} {
  const tabs = asArray(children).filter((c) => !!c) as MainTab<T>[];

  const [tabIds, tabNames] = tabs.reduce(
    ([ids, names], tab) => [
      ids.concat(tab.props.id),
      names.concat(tab.props.displayName),
    ],
    [[], []] as [
      T[],
      (JSX.Element | string | React.FC<{ isSelected: boolean }>)[]
    ]
  );

  return { tabs, tabIds, tabNames };
}
